"use client"

import { useState, useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import { DayPicker } from "react-day-picker"
import { es as esRdp } from "react-day-picker/locale"
import { format, startOfDay } from "date-fns"
import { es as esDateFns } from "date-fns/locale/es"
import { Calendar, Clock, Check, X } from "@/lib/icons"
import { Button } from "@/components/ui/button"

// Paleta tinto corporativa
const TINTO = {
    50: "#fdf2f4",
    100: "#fce7eb",
    200: "#f9cdd6",
    300: "#f4a3b5",
    400: "#ed6d8a",
    500: "#d94468",
    600: "#b82d53",
    700: "#7a1b33",
    800: "#6b1829",
    900: "#5a1523",
    950: "#320a12",
}

export function DateTimePicker({ value, onChange, onConfirm, placeholder = "Seleccionar fecha y hora", minDate, variant = "cita" }) {
    const isTask = variant === "task"
    const headerEyebrow = isTask ? "Fecha límite" : "Agendar cita"
    const confirmLabel = isTask ? "Confirmar" : "Confirmar Cita"
    const [open, setOpen] = useState(false)
    const [selectedDate, setSelectedDate] = useState(null)
    const [hour, setHour] = useState(10)
    const [minute, setMinute] = useState(0)
    const triggerRef = useRef(null)
    const popoverRef = useRef(null)

    useEffect(() => {
        if (value) {
            const d = new Date(value)
            if (!isNaN(d.getTime())) {
                setSelectedDate(d)
                setHour(d.getHours())
                setMinute(d.getMinutes())
            }
        } else {
            setSelectedDate(null)
            setHour(10)
            setMinute(0)
        }
    }, [value])

    useEffect(() => {
        if (!open) return
        const handleClick = (e) => {
            if (
                popoverRef.current && !popoverRef.current.contains(e.target) &&
                triggerRef.current && !triggerRef.current.contains(e.target)
            ) {
                setOpen(false)
            }
        }
        document.addEventListener("mousedown", handleClick)
        return () => document.removeEventListener("mousedown", handleClick)
    }, [open])

    const buildDateTime = (date, h, m) => {
        if (!date) return null
        const d = new Date(date)
        d.setHours(h, m, 0, 0)
        return d.toISOString()
    }

    const handleDaySelect = (day) => {
        if (!day) return
        setSelectedDate(day)
        const iso = buildDateTime(day, hour, minute)
        onChange?.(iso)
    }

    const handleHourChange = (newHour) => {
        setHour(newHour)
        if (selectedDate) {
            const iso = buildDateTime(selectedDate, newHour, minute)
            onChange?.(iso)
        }
    }

    const handleMinuteChange = (newMinute) => {
        setMinute(newMinute)
        if (selectedDate) {
            const iso = buildDateTime(selectedDate, hour, newMinute)
            onChange?.(iso)
        }
    }

    const handleConfirm = () => {
        if (!selectedDate) return
        const iso = buildDateTime(selectedDate, hour, minute)
        onChange?.(iso)
        onConfirm?.(iso)
        setOpen(false)
    }

    const handleClear = () => {
        setSelectedDate(null)
        setHour(10)
        setMinute(0)
        onChange?.(null)
        setOpen(false)
    }

    const today = startOfDay(new Date())
    const disabledDays = minDate ? { before: minDate } : { before: today }

    const displayText = selectedDate
        ? format(selectedDate, "d 'de' MMMM yyyy", { locale: esDateFns }) + ` · ${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`
        : placeholder

    // Horario: 8:00 AM a 4:00 PM
    const HOURS = Array.from({ length: 9 }, (_, i) => i + 8) // 8, 9, 10, ..., 16
    const MINUTES = [0, 15, 30, 45]

    const formatHourLabel = (h) => {
        if (h === 12) return "12 pm"
        if (h > 12) return `${h - 12} pm`
        return `${h} am`
    }

    return (
        <div className="relative">
            {/* Trigger */}
            <button
                ref={triggerRef}
                type="button"
                onClick={() => setOpen(!open)}
                className={`
                    flex items-center gap-2 w-full h-9 px-3 rounded-xl border text-left text-[12px] font-medium
                    transition-all duration-200 cursor-pointer
                    ${selectedDate
                        ? "border-rose-300/80 dark:border-rose-700/60 bg-rose-50/60 dark:bg-rose-950/20 text-rose-800 dark:text-rose-300 shadow-sm shadow-rose-100/50 dark:shadow-rose-900/20"
                        : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 hover:border-rose-200 dark:hover:border-rose-800/50"
                    }
                `}
            >
                <Calendar className={`h-3.5 w-3.5 flex-shrink-0 ${selectedDate ? "text-rose-700 dark:text-rose-400" : "text-slate-400"}`} />
                <span className="flex-1 truncate">{displayText}</span>
                {selectedDate && (
                    <span
                        onClick={(e) => { e.stopPropagation(); handleClear() }}
                        className="flex-shrink-0 p-0.5 rounded-full hover:bg-rose-100 dark:hover:bg-rose-900/30 transition-colors"
                    >
                        <X className="h-3 w-3 text-rose-400" />
                    </span>
                )}
            </button>

            {/* Popover Calendar */}
            {open && createPortal(
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setOpen(false)} />

                    {/* Calendar Panel */}
                    <div
                        ref={popoverRef}
                        className="relative z-10 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl shadow-black/15 dark:shadow-black/40 border border-slate-200/50 dark:border-slate-700/50 overflow-hidden w-[360px] animate-in fade-in-0 zoom-in-95 duration-200"
                    >
                        {/* Header con gradiente tinto */}
                        <div
                            className="px-5 py-4"
                            style={{ background: `linear-gradient(135deg, ${TINTO[700]}, ${TINTO[800]})` }}
                        >
                            <p className="text-white/60 text-[10px] font-semibold uppercase tracking-widest">{headerEyebrow}</p>
                            <p className="text-white text-base font-bold mt-1 capitalize">
                                {selectedDate
                                    ? format(selectedDate, "EEEE, d 'de' MMMM", { locale: esDateFns })
                                    : "Selecciona una fecha"}
                            </p>
                            {selectedDate && (
                                <div className="flex items-center gap-1.5 mt-1.5">
                                    <div className="flex items-center gap-1 bg-white/15 backdrop-blur-sm rounded-lg px-2.5 py-1">
                                        <Clock className="h-3 w-3 text-white/80" />
                                        <span className="text-white text-xs font-bold tabular-nums">
                                            {String(hour).padStart(2, "0")}:{String(minute).padStart(2, "0")} hrs
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Calendar */}
                        <div className="px-3 pt-3 pb-1 flex justify-center">
                            <DayPicker
                                locale={esRdp}
                                mode="single"
                                selected={selectedDate}
                                onSelect={handleDaySelect}
                                disabled={disabledDays}
                                showOutsideDays
                                classNames={{
                                    root: "rdp-custom",
                                    months: "flex flex-col",
                                    month: "space-y-2",
                                    month_caption: "flex justify-center items-center h-8",
                                    caption_label: "text-sm font-bold text-slate-800 dark:text-slate-200 capitalize",
                                    nav: "flex items-center gap-1",
                                    button_previous: "h-7 w-7 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center hover:bg-rose-50 hover:text-rose-700 dark:hover:bg-rose-950/30 dark:hover:text-rose-300 transition-colors text-slate-600 dark:text-slate-400",
                                    button_next: "h-7 w-7 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center hover:bg-rose-50 hover:text-rose-700 dark:hover:bg-rose-950/30 dark:hover:text-rose-300 transition-colors text-slate-600 dark:text-slate-400",
                                    weekdays: "flex",
                                    weekday: "text-[10px] font-bold uppercase text-slate-400 dark:text-slate-500 w-[44px] text-center",
                                    week: "flex",
                                    day: "p-0 text-center",
                                    day_button: `
                                        h-9 w-[44px] rounded-lg text-[13px] font-medium cursor-pointer
                                        transition-all duration-150
                                        text-slate-700 dark:text-slate-300
                                        hover:bg-rose-50 hover:text-rose-800
                                        dark:hover:bg-rose-950/20 dark:hover:text-rose-300
                                    `,
                                    selected: "!bg-[var(--rdp-accent-color)] !text-white !font-bold !shadow-md !shadow-rose-900/25 !rounded-lg",
                                    today: "ring-2 ring-rose-300/50 dark:ring-rose-700/40 ring-inset rounded-lg font-bold text-rose-200 dark:text-rose-400",
                                    outside: "text-slate-300 dark:text-slate-600 opacity-40",
                                    disabled: "text-slate-200 dark:text-slate-700 cursor-not-allowed opacity-25 hover:bg-transparent",
                                }}
                            />
                        </div>

                        {/* Time Picker */}
                        <div className="px-4 pb-4 pt-1">
                            <div className="flex items-center gap-2 mb-2.5">
                                <div className="h-5 w-5 rounded-md flex items-center justify-center" style={{ backgroundColor: TINTO[50] }}>
                                    <Clock className="h-3 w-3" style={{ color: TINTO[700] }} />
                                </div>
                                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Horario</span>
                            </div>

                            {/* Hour Selector - Grid compacto */}
                            <div className="grid grid-cols-6 gap-1 mb-2.5">
                                {HOURS.map((h) => {
                                    const isSelected = h === hour
                                    return (
                                        <button
                                            key={h}
                                            type="button"
                                            onClick={() => handleHourChange(h)}
                                            className={`
                                                h-8 rounded-lg text-[11px] font-semibold transition-all duration-150 relative
                                                ${isSelected
                                                    ? "text-white shadow-md"
                                                    : "bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-rose-50 hover:text-rose-700 dark:hover:bg-rose-950/20 dark:hover:text-rose-300"
                                                }
                                            `}
                                            style={isSelected ? { backgroundColor: TINTO[700], boxShadow: `0 4px 12px ${TINTO[700]}40` } : {}}
                                        >
                                            {formatHourLabel(h)}
                                        </button>
                                    )
                                })}
                            </div>

                            {/* Minute Selector */}
                            <div className="flex gap-1.5 mb-4">
                                {MINUTES.map((m) => {
                                    const isSelected = m === minute
                                    return (
                                        <button
                                            key={m}
                                            type="button"
                                            onClick={() => handleMinuteChange(m)}
                                            className={`
                                                flex-1 h-9 rounded-lg text-[12px] font-bold transition-all duration-150
                                                ${isSelected
                                                    ? "text-white shadow-md"
                                                    : "bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-rose-50 hover:text-rose-700 dark:hover:bg-rose-950/20 dark:hover:text-rose-300"
                                                }
                                            `}
                                            style={isSelected ? { backgroundColor: TINTO[700], boxShadow: `0 4px 12px ${TINTO[700]}40` } : {}}
                                        >
                                            :{String(m).padStart(2, "0")}
                                        </button>
                                    )
                                })}
                            </div>

                            {/* Actions */}
                            <div className="flex gap-2.5 pt-3 border-t border-slate-100 dark:border-slate-800">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="flex-1 h-10 rounded-xl text-xs font-semibold border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                                    onClick={() => setOpen(false)}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    type="button"
                                    size="sm"
                                    className="flex-1 h-10 rounded-xl text-xs font-bold text-white shadow-lg disabled:opacity-40 transition-all hover:shadow-xl"
                                    style={{
                                        backgroundColor: TINTO[700],
                                        boxShadow: selectedDate ? `0 8px 24px ${TINTO[700]}35` : undefined,
                                    }}
                                    onClick={handleConfirm}
                                    disabled={!selectedDate}
                                >
                                    <Check className="h-3.5 w-3.5 mr-1.5" />
                                    {confirmLabel}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    )
}

export default DateTimePicker
